import {
  CellType,
  ComponentType,
  IListComponentModel,
  IMediaModel,
  Orientation,
} from "@nf/common";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useMemo } from "react";

import MediaActionButtons from "components/Media/MediaActionButtons";
import MediaCoverImage from "components/Media/MediaCoverImage";
import MediaDescription from "components/Media/MediaDescription";
import MediaDetailsAdditionalInfo from "components/Media/MediaDetailsAdditionalInfo";
import MediaDetailsScreen from "screens/MediaDetailsScreen";

import styles from "./SeriesDetailsScreen.module.scss";

const GridComponent = dynamic(
  () =>
    import("components/Configuration/ListComponent/components/GridComponent")
);
const EpisodeListItem = dynamic(() => import("components/EpisodeListItem"));
const SeasonSelect = dynamic(() => import("components/SeasonSelect"), {
  ssr: false,
});

interface SeriesDetailsScreenProps {
  media: IMediaModel;
}

export const SeriesDetailsScreen = ({ media }: SeriesDetailsScreenProps) => {
  const router = useRouter();

  const seasons = media?.Media;
  const selectedSeason = router.query.season as string | undefined;

  const episodes = useMemo(() => {
    const list: IListComponentModel = {
      ComponentTypeCode: ComponentType.List,
      CellType: CellType.Custom,
      Orientation: Orientation.Grid,
      MediaList: [],
      CutomListItem: <EpisodeListItem />,
    };

    if (selectedSeason) {
      const season = media.Media?.find((row) => row.Id === selectedSeason);

      if (season) {
        list.MediaList = season.Media || [];
      }
    } else {
      list.MediaList = media.Media?.[0]?.Media ?? [];
    }

    return list;
  }, [media]);

  const changeSeason = (seasonId: string | number) => {
    router.replace(
      {
        query: {
          ...router.query,
          season: seasonId,
        },
      },
      undefined,
      { scroll: false, shallow: true }
    );
  };

  if (!media) {
    return null;
  }

  return (
    <MediaDetailsScreen media={media}>
      <div className={styles.container}>
        <div className={styles.content}>
          <MediaDetailsAdditionalInfo media={media} />

          <MediaActionButtons media={media} />

          <MediaDescription media={media} />

          {seasons && (
            <SeasonSelect
              seasons={seasons}
              selected={selectedSeason}
              onChange={changeSeason}
            />
          )}
        </div>

        <div className={styles.cover}>
          <MediaCoverImage media={media} />
        </div>

        <GridComponent
          className={styles.episodes}
          responsive={{
            phone: { cols: 1, gap: "8px" },
            tablet: { cols: 2, gap: "8px" },
            desktop: { cols: 3, gap: "16px" },
          }}
          component={episodes}
        />
      </div>
    </MediaDetailsScreen>
  );
};

export default SeriesDetailsScreen;
